import type { JSX } from 'react';
import type { FieldPath, FieldPathValue, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';
import type { FormRadioProps } from 'ts/components/Form';
import { FormRadio } from 'ts/components/Form';

/** Props for HookFormRadio. */
type HookFormRadioProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Pick<
	UseControllerProps<TFieldValues, TName>,
	'name' | 'control'
> &
	Omit<FormRadioProps, 'name'>;

/** This is a wrapper around Form.Radio that is compatible with react-hook-form. */
export function HookFormRadio<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ name, control, ...radioProps }: HookFormRadioProps<TFieldValues, TName>): JSX.Element {
	const { field, fieldState } = useController<TFieldValues>({ name, control });
	return (
		<FormRadio
			name={name}
			onChange={(e, { value }) => field.onChange(value as FieldPathValue<TFieldValues, TName>)}
			checked={field.value === radioProps.value}
			error={fieldState.error?.message || fieldState.invalid}
			{...radioProps}
		/>
	);
}
